/** Restore an earlier saved version (the server keeps the last 50), or a local unsaved draft. */

import type { Project } from "@studio/engine";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../api";
import { useEditor } from "./store";

type Version = { file: string; time: number; revision: number; scenes: number; elements: number };

/** Replace the project's content with `p` as one undoable edit (id and revision stay). */
export function restoreProject(p: Project) {
  useEditor.getState().edit((d) => {
    const { id } = d;
    Object.assign(d, { ...p, id, revision: d.revision });
  });
  useEditor.getState().set({ selection: null });
}

export function HistoryDialog() {
  const project = useEditor((s) => s.project)!;
  const [list, setList] = useState<Version[] | null>(null);
  const [err, setErr] = useState("");
  const close = () => useEditor.getState().set({ panel: null });
  useEffect(() => {
    api.history(project.id).then(setList).catch((e) => setErr(e.message));
  }, [project.id]);
  const restore = async (v: Version) => {
    try {
      restoreProject(await api.historyVersion(project.id, v.file));
      close();
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  return (
    <div className="modal-back" onPointerDown={close}>
      <div className="modal history" onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <h3>Version history</h3>
          <button className="icon" onClick={close}><X size={18} /></button>
        </header>
        <p className="muted small">Each save keeps the version it replaced (the last 50). Restoring is one step you can undo.</p>
        {err && <p className="bad">{err}</p>}
        {list === null ? (
          <p className="muted">Loading…</p>
        ) : list.length === 0 ? (
          <p className="muted">No earlier versions yet.</p>
        ) : (
          <div className="import-table">
            <table>
              <thead><tr><th>Saved</th><th>Revision</th><th>Scenes</th><th>Elements</th><th /></tr></thead>
              <tbody>
                {list.map((v) => (
                  <tr key={v.file}>
                    <td>{new Date(v.time * 1000).toLocaleString()}</td>
                    <td>{v.revision}</td>
                    <td>{v.scenes}</td>
                    <td>{v.elements}</td>
                    <td><button onClick={() => restore(v)}>Restore</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
